/**
 * ApprovedVsSettledRouter - the element behind /reports/approved-vs-settled.
 *
 * ONE route, ONE slug, the SAME guards (RequireTenantReport
 * `approved_vs_settled` + RequireReportAccess). A per-tenant flag picks the
 * page inside them:
 *
 *   featureFlags.approved_vs_settled_v2 === 'enabled'   ApprovedVsSettledV2Page
 *   absent / 'off' / true / anything else              ApprovedVsSettledPage,
 *                                                      exactly as today
 *
 * The flag is parsed by the shared contract (parseApprovedVsSettledV2Mode), so
 * the client and the callables agree on what 'enabled' means. The v2 page is
 * React.lazy: tenants on today's page never download its bundle.
 *
 * No flash of the wrong page: AppLayout renders <Outlet> only after the
 * company document has loaded, so companyData (and its featureFlags) is
 * present when this mounts. If it ever were not, the answer is today's page,
 * which is the flag-off answer.
 */

import React, { Suspense } from 'react';
import { parseApprovedVsSettledV2Mode, type ApprovedVsSettledV2Mode } from '@valutten/shared-schemas/dist/approvedVsSettledV2';
import ApprovedVsSettledPage from './ApprovedVsSettledPage';
import { useAppContext } from '../../components/layout/AppLayout';
import { SkeletonReportPage } from '../../components/common/SkeletonLoader';

// Code-split: tenants on today's page never pay for this bundle.
const ApprovedVsSettledV2Page = React.lazy(() => import('./ApprovedVsSettledV2Page'));

/** PURE. Exported so the decision is tested without a router or a page. */
export function resolveApprovedVsSettledVersion(
  featureFlags: Record<string, unknown> | null | undefined,
): ApprovedVsSettledV2Mode {
  return parseApprovedVsSettledV2Mode(featureFlags?.approved_vs_settled_v2);
}

const ApprovedVsSettledRouter: React.FC = () => {
  const ctx = useAppContext() as { companyData?: { featureFlags?: Record<string, unknown> } | null } | undefined;

  if (resolveApprovedVsSettledVersion(ctx?.companyData?.featureFlags) !== 'enabled') {
    return <ApprovedVsSettledPage />;
  }

  return (
    <Suspense fallback={<SkeletonReportPage statCards={5} statColumns={3} tableColumns={8} />}>
      <ApprovedVsSettledV2Page />
    </Suspense>
  );
};

export default ApprovedVsSettledRouter;
